import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchOpportunityDetail,
  type OpportunityDetail,
  type OpportunityLineItem,
} from '../../lib/opportunities';
import {
  OPPORTUNITY_STAGE_COLORS,
  OPPORTUNITY_TYPE_COLORS,
  SALESFORCE_OPP_URL,
} from '../../lib/opportunity-constants';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/format';
import { usePhone } from '../../contexts/PhoneProvider';

type Variant = 'page' | 'drawer';

interface OpportunityDetailViewProps {
  id: string;
  variant: Variant;
  onClose?: () => void;
}

function PhoneButton({ phone, onDial }: { phone: string; onDial: () => void }) {
  if (!phone) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-900">{phone}</span>
      <button
        onClick={onDial}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors font-medium"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
        </svg>
        Bellen
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-slate-900">{children || '—'}</div>
    </div>
  );
}

function LineItemRow({ item }: { item: OpportunityLineItem }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-3 align-top">
        <div className="text-sm font-medium text-slate-900">{item.productName || 'Regel'}</div>
        {item.description && (
          <div className="text-xs text-slate-500 italic whitespace-pre-wrap mt-1">{item.description}</div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-700 text-right align-top">{item.quantity}</td>
      <td className="px-4 py-3 text-sm text-slate-700 text-right align-top">{formatCurrency(item.unitPrice)}</td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900 text-right align-top">{formatCurrency(item.totalPrice)}</td>
    </tr>
  );
}

/**
 * Gedeelde opportunity-detail-view. Gebruikt door OpportunityDetailPage (route
 * /opportunities/:id, variant='page') én OpportunityDetailDrawerPanel
 * (klantpagina-tab Doorverwijzingen, variant='drawer'). Mirror van
 * OrderDetailView. De onderliggende API (/api/opportunities/detail) vereist
 * alleen een geldig token — geen admin — dus de drawer werkt voor elke
 * medewerker.
 */
export function OpportunityDetailView({ id, variant, onClose }: OpportunityDetailViewProps) {
  const isDrawer = variant === 'drawer';
  const navigate = useNavigate();
  const { dial } = usePhone();
  const [detail, setDetail] = useState<OpportunityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOpportunityDetail(id)
      .then((d) => {
        if (!cancelled) {
          setDetail(d);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Kon opportunity niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Drawer krijgt een sticky kop met sluitknop; de page een terug-link.
  const header = isDrawer ? (
    <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
      <button onClick={onClose} aria-label="Sluiten" className="text-slate-400 hover:text-slate-600">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <h2 className="text-sm font-semibold text-slate-900 truncate">Doorverwijzing</h2>
      {detail?.id && (
        <Link
          to={`/opportunities/${detail.id}`}
          onClick={onClose}
          className="ml-auto text-xs text-indigo-600 hover:underline flex-shrink-0"
        >
          Open volledig →
        </Link>
      )}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className={clsx(isDrawer && 'flex-1 flex flex-col min-h-0')}>
        {header}
        <div className="p-6 max-w-5xl mx-auto w-full space-y-4 animate-pulse">
          <div className="h-28 bg-slate-100 rounded" />
          <div className="h-40 bg-slate-100 rounded" />
          <div className="h-40 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className={clsx(isDrawer && 'flex-1 flex flex-col min-h-0')}>
        {header}
        <div className="p-6 max-w-5xl mx-auto w-full">
          {!isDrawer && (
            <button
              onClick={() => navigate('/opportunities')}
              className="text-sm text-slate-500 hover:text-slate-700 mb-4"
            >
              ← Terug naar Opportunities
            </button>
          )}
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error || 'Opportunity niet gevonden'}
          </div>
        </div>
      </div>
    );
  }

  const { customer, partner, financials, tracking, lineItems } = detail;

  const hasAnyNote =
    !!detail.description ||
    !!detail.notesExecution ||
    !!detail.notesOffer ||
    !!detail.partnerRemarks;

  const subtotaalExcl = financials.totalInclBtw - financials.totalBtwAmount;

  return (
    <div className={clsx(isDrawer && 'flex-1 flex flex-col min-h-0')}>
      {header}
      <div className="flex-1 overflow-y-auto">
        <div className={clsx('mx-auto space-y-6', isDrawer ? 'p-4' : 'max-w-5xl p-6')}>
          {!isDrawer && (
            <div>
              <button
                onClick={() => navigate('/opportunities')}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                ← Terug naar Opportunities
              </button>
            </div>
          )}

          {/* Header-card */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-semibold text-slate-900">
                    {customer.name || detail.name || '(geen klant)'}
                  </h1>
                  <span className="text-sm text-slate-500">
                    {detail.opportunityNumber || detail.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {detail.stage && (
                    <span
                      className={clsx(
                        'text-xs px-2 py-0.5 rounded font-medium',
                        OPPORTUNITY_STAGE_COLORS[detail.stage] || 'bg-slate-100 text-slate-600'
                      )}
                    >
                      {detail.stage}
                    </span>
                  )}
                  {detail.type && (
                    <span
                      className={clsx(
                        'text-xs px-2 py-0.5 rounded font-medium',
                        OPPORTUNITY_TYPE_COLORS[detail.type] || 'bg-slate-100 text-slate-600'
                      )}
                    >
                      {detail.type}
                    </span>
                  )}
                  {detail.brand && (
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-700">
                      {detail.brand}
                    </span>
                  )}
                  {detail.channel && (
                    <span className="text-xs text-slate-500">· {detail.channel}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  Aangemaakt {formatDateTime(detail.createdDate)}
                  {detail.ownerName && ` · ${detail.ownerName}`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold text-slate-900">
                  {formatCurrency(financials.totalInclBtw)}
                </div>
                <div className="text-xs text-slate-500">
                  {formatCurrency(financials.amount)} excl. BTW
                </div>
                <a
                  href={SALESFORCE_OPP_URL(detail.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-indigo-600 hover:underline"
                >
                  Open in Salesforce ↗
                </a>
              </div>
            </div>
          </div>

          {/* Aanvraag */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-900">
                Aanvraag ({lineItems.length} {lineItems.length === 1 ? 'regel' : 'regels'})
              </h2>
            </div>
            {lineItems.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Product</th>
                    <th className="text-right font-medium px-4 py-2">Aantal</th>
                    <th className="text-right font-medium px-4 py-2">Prijs p/st</th>
                    <th className="text-right font-medium px-4 py-2">Subtotaal</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li) => (
                    <LineItemRow key={li.id} item={li} />
                  ))}
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="px-4 py-2 text-sm font-medium text-slate-700" colSpan={3}>
                      Subtotaal excl. BTW
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-slate-900 text-right">
                      {formatCurrency(subtotaalExcl)}
                    </td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2 text-sm text-slate-700" colSpan={3}>
                      BTW
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-700 text-right">
                      {formatCurrency(financials.totalBtwAmount)}
                    </td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2 text-sm font-semibold text-slate-900" colSpan={3}>
                      Totaal incl. BTW
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-slate-900 text-right">
                      {formatCurrency(financials.totalInclBtw)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-6 text-sm text-slate-500">Geen regels</div>
            )}
          </div>

          {/* Klant */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Klant</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Field label="Naam">{customer.name}</Field>
                <Field label="E-mail">
                  {customer.email ? (
                    <a href={`mailto:${customer.email}`} className="text-indigo-600 hover:underline">
                      {customer.email}
                    </a>
                  ) : null}
                </Field>
                <Field label="Telefoon">
                  <PhoneButton
                    phone={customer.phone}
                    onDial={() =>
                      dial(customer.phone, {
                        brand: 'CINCO',
                        customerId: customer.accountId,
                        customerName: customer.name,
                        sourceContext: 'opportunity-detail-customer',
                      })
                    }
                  />
                </Field>
              </div>
              <div className="space-y-3">
                <Field label="Projectadres">
                  <div>
                    {customer.projectStreet && <div>{customer.projectStreet}</div>}
                    {(customer.projectPostcode || customer.projectCity) && (
                      <div>
                        {[customer.projectPostcode, customer.projectCity].filter(Boolean).join(' ')}
                      </div>
                    )}
                    {!customer.projectStreet && !customer.projectPostcode && !customer.projectCity && null}
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* Partner */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="text-sm font-semibold text-slate-900">Partner</h2>
              {detail.workOrderId && (
                <Link
                  to={`/orders/${detail.workOrderId}`}
                  onClick={onClose}
                  className="text-xs px-3 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium"
                >
                  Naar Werk Order →
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Field label="Partner">
                  <div>
                    <div className="font-medium">{partner.name || '—'}</div>
                    {partner.contactName && (
                      <div className="text-xs text-slate-500 mt-0.5">{partner.contactName}</div>
                    )}
                  </div>
                </Field>
                <Field label="E-mail">
                  {partner.email ? (
                    <a href={`mailto:${partner.email}`} className="text-indigo-600 hover:underline">
                      {partner.email}
                    </a>
                  ) : null}
                </Field>
                <Field label="Telefoon">
                  <PhoneButton
                    phone={partner.phone}
                    onDial={() =>
                      dial(partner.phone, {
                        brand: 'CINCO',
                        customerId: partner.accountId || undefined,
                        customerName: partner.name,
                        sourceContext: 'opportunity-detail-partner',
                      })
                    }
                  />
                </Field>
              </div>
              <div className="space-y-3">
                <Field label="Doorverwezen op">
                  {partner.sentAt ? formatDateTime(partner.sentAt) : null}
                </Field>
                {detail.workOrderId && detail.workOrderName && (
                  <Field label="Werk Order">
                    <Link to={`/orders/${detail.workOrderId}`} onClick={onClose} className="text-indigo-600 hover:underline">
                      {detail.workOrderName}
                    </Link>
                  </Field>
                )}
              </div>
            </div>
          </div>

          {/* Commercieel */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Commercieel</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Totaal excl. BTW">{formatCurrency(financials.amount)}</Field>
              <Field label="Totaal incl. BTW">{formatCurrency(financials.totalInclBtw)}</Field>
              <Field label="BTW-bedrag">{formatCurrency(financials.totalBtwAmount)}</Field>
              <Field label="Commissie">{formatCurrency(financials.commissionAmount)}</Field>
              <Field label="Cinco Share">{formatCurrency(financials.cincoShare)}</Field>
              <Field label="Payout">{financials.payoutStatus || '—'}</Field>
              <Field label="Gefactureerd">
                <span
                  className={clsx(
                    'text-xs px-2 py-0.5 rounded font-medium',
                    financials.gefactureerd ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  {financials.gefactureerd ? 'Ja' : 'Nee'}
                </span>
              </Field>
              {detail.plannedExecutionDate && (
                <Field label="Gepland uitvoering">{formatDate(detail.plannedExecutionDate)}</Field>
              )}
            </div>
            {detail.reasonLost && (
              <div className="mt-4">
                <span className="text-xs px-2 py-1 rounded font-medium bg-red-100 text-red-700">
                  Reden verloren: {detail.reasonLost}
                </span>
              </div>
            )}
          </div>

          {/* Opmerkingen */}
          {hasAnyNote && (
            <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Opmerkingen</h2>
              {detail.description && (
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Beschrijving</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{detail.description}</div>
                </div>
              )}
              {detail.notesExecution && (
                <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <div className="text-xs text-amber-700 uppercase tracking-wide mb-1">Voor uitvoering</div>
                  <div className="text-sm text-amber-900 whitespace-pre-wrap">{detail.notesExecution}</div>
                </div>
              )}
              {detail.notesOffer && (
                <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2">
                  <div className="text-xs text-blue-700 uppercase tracking-wide mb-1">Offerte</div>
                  <div className="text-sm text-blue-900 whitespace-pre-wrap">{detail.notesOffer}</div>
                </div>
              )}
              {detail.partnerRemarks && (
                <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  <div className="text-xs text-slate-600 uppercase tracking-wide mb-1">Partner-opmerkingen</div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{detail.partnerRemarks}</div>
                </div>
              )}
            </div>
          )}

          {/* Tracking */}
          <details className="bg-white rounded-lg border border-slate-200 p-6">
            <summary className="text-sm font-semibold text-slate-900 cursor-pointer select-none">
              Tracking & bron
            </summary>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              <Field label="Lead Source">{tracking.leadSource}</Field>
              <Field label="Primary Source">{tracking.primarySource}</Field>
              <Field label="Web Source">{tracking.webSource}</Field>
              <Field label="UTM Source">{tracking.utmSource}</Field>
              <Field label="UTM Medium">{tracking.utmMedium}</Field>
              <Field label="UTM Campaign">{tracking.utmCampaign}</Field>
              <Field label="UTM Term">{tracking.utmTerm}</Field>
              <Field label="UTM Content">{tracking.utmContent}</Field>
              <Field label="GCLID">{tracking.gclid}</Field>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
